import React from 'react';
import { mergeProps, useField } from 'react-aria';

import type { CheckboxInputProps } from '../../components/CheckboxInput/CheckboxInput';
import type { BaseFieldProps } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { Badge } from '../../components/Badge/Badge';
import { CheckboxInput } from '../../components/CheckboxInput/CheckboxInput';
import { styled } from '../../stitches.config';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { FieldDivider, fieldDividerDisplay } from './common/FieldDivider';
import { SecondaryFieldDescription, SecondaryFieldLabel } from './common/FieldLabel';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';
import { MaybeFieldContainer } from './FieldContainer';
import { FieldErrorMessages } from './FieldError';

const SecondaryCheckboxFieldParent = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
  width: '100%',
});
SecondaryCheckboxFieldParent.displayName = 'SecondaryCheckboxFieldParent';

const SecondaryCheckboxFieldLabelling = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});
SecondaryCheckboxFieldLabelling.displayName = 'SecondaryCheckboxFieldLabelling';

const SecondaryCheckboxFieldStart = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',
  width: '100%',
});
SecondaryCheckboxFieldStart.displayName = 'SecondaryCheckboxFieldStart';

const SecondaryCheckboxFieldEnd = styled('div', {
  maxHeight: '$20',
});
SecondaryCheckboxFieldEnd.displayName = 'SecondaryCheckboxFieldEnd';

const SecondaryCheckboxFieldAlert = styled(Alert, {
  padding: '$8 $16',
});
SecondaryCheckboxFieldAlert.displayName = 'SecondaryCheckboxFieldAlert';

const SecondaryCheckboxFieldContainer = styled('div', {
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  padding: '$10 $16',
  [fieldDividerDisplay]: 'none',

  '&:not(:last-child)': {
    paddingBottom: 0,
    [fieldDividerDisplay]: 'flex',
  },
});
SecondaryCheckboxFieldContainer.displayName = 'SecondaryCheckboxFieldContainer';

export interface SecondaryCheckboxFieldProps extends BaseFieldProps, CheckboxInputProps {
  internal?: boolean;
}

export function SecondaryCheckboxField(props: SecondaryCheckboxFieldProps) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();

  const {
    description,
    errorMessage = customFieldProps.errorMessage,
    internal,
    label = customFieldProps.label,
    ...extraInputProps
  } = props;

  const {
    fieldProps: inputProps,
    descriptionProps,
    errorMessageProps,
    labelProps,
  } = useField({
    description,
    errorMessage,
    label,
  });

  return (
    <MaybeFieldContainer>
      <SecondaryCheckboxFieldContainer>
        <SecondaryCheckboxFieldParent>
          <SecondaryCheckboxFieldEnd>
            <CheckboxInput {...mergeProps(customInputProps, inputProps, extraInputProps)} />
          </SecondaryCheckboxFieldEnd>
          <SecondaryCheckboxFieldStart>
            <SecondaryCheckboxFieldLabelling>
              <SecondaryFieldLabel {...labelProps}>{label}</SecondaryFieldLabel>
              {internal && (
                <Badge ends="card" size="small" internal>
                  Internal
                </Badge>
              )}
            </SecondaryCheckboxFieldLabelling>
            {description && (
              <SecondaryFieldDescription {...descriptionProps}>
                {description}
              </SecondaryFieldDescription>
            )}
          </SecondaryCheckboxFieldStart>
        </SecondaryCheckboxFieldParent>
        <FieldDivider context="secondary" />
      </SecondaryCheckboxFieldContainer>
      {errorMessage && (
        <FieldErrorMessages>
          <SecondaryCheckboxFieldAlert
            {...errorMessageProps}
            type="inline"
            relation="stacked"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        </FieldErrorMessages>
      )}
    </MaybeFieldContainer>
  );
}
